/**
 * Desktop notifications for the agent workspace — Step 138 (browser half).
 *
 * Kai: *"notification & browser notifications in chrome are a medium task,
 * please add."*
 *
 * The bell (Step 125) only helps an agent who is looking at it. This is what
 * reaches somebody who has the tab open behind their mail client — which is
 * where a service desk actually lives all day.
 *
 * ── What this is NOT ───────────────────────────────────────────────────
 *
 * This is the Notification API, not Web Push. It fires only while the app is
 * open in a tab. It will not reach a closed laptop at 02:00 — that needs a
 * service worker, VAPID keys and a subscription table, and it is the half of
 * Step 138 that pairs with the on-call ladder. Stated plainly here because
 * "we have browser notifications" is exactly the sentence that later gets
 * mistaken for "somebody will be woken up".
 *
 * ── Permission is asked on a CLICK, never on load ──────────────────────
 *
 * Chrome ignores `requestPermission()` outside a user gesture, and a dismissed
 * or denied prompt is STICKY — the site cannot ask again, and the user has to
 * go into site settings to undo it. So an app that prompts on load spends that
 * one chance on somebody who has no idea yet what the notifications are for.
 * The bell offers it instead, once, with the reason next to it.
 *
 * ── They fire whether or not the tab is in front ───────────────────────
 *
 * Kai, 2026-09-14: *"Make notifications appear even if I'm on that tab
 * though please."* This overrides the original rule, which suppressed a
 * notification while the app was visible on the reasoning that the bell had
 * already said it.
 *
 * His call, and the reasoning behind it is sound for a service desk: an agent
 * with the queue open in front of them is not necessarily LOOKING at it, and
 * the assignment they need to see is the one that arrives while they are
 * reading a different ticket in the same tab. The bell's unread dot is easy to
 * miss when your eyes are in the thread.
 *
 * The cost is real and worth stating rather than discovering: a desktop
 * notification about something already on screen is noise, and noise is what
 * makes people revoke the permission for good — a decision that is sticky and
 * that we cannot ask them to reverse. The per-user "Show these on my desktop"
 * switch in the bell is the escape hatch, which is why it defaults on but
 * stays switchable.
 *
 * ── The first poll is a BASELINE, not a backlog ────────────────────────
 *
 * Without this, opening the workspace in the morning fires a notification for
 * every unread from overnight — twenty at once, which is both useless and the
 * fastest way to have the permission taken away. The first run records what
 * was already there; only genuinely new rows notify after that.
 */
import { useEffect, useRef, useState } from 'react';

const PREF_KEY = 'itsm.desktop-notifications';

export type NotificationPermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

export function browserNotificationState(): NotificationPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission as NotificationPermissionState;
}

/** The user's own on/off, independent of the browser permission. Granting
 *  Chrome the right to show them is not the same as wanting them today. */
export function desktopNotificationsEnabled(): boolean {
  try {
    // Defaults ON once permission is granted: somebody who went through the
    // prompt has already said what they want, and making them say it twice
    // reads as the feature being broken.
    return localStorage.getItem(PREF_KEY) !== 'off';
  } catch {
    // Private windows and blocked site data throw rather than returning null.
    return true;
  }
}

export function setDesktopNotificationsEnabled(on: boolean): void {
  try {
    localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
  } catch {
    /* A preference we cannot persist is not worth failing a render over. */
  }
}

export interface NotifiableRow {
  id: string;
  title: string;
  body: string;
  ticketId: string | null;
  readAt: number | null;
}

/**
 * Raise a desktop notification for anything newly unread.
 *
 * `onOpen` runs when one is clicked — the caller navigates, because routing
 * belongs to the component tree rather than to this hook.
 */
export function useBrowserNotifications(
  rows: NotifiableRow[],
  onOpen: (ticketId: string) => void,
  /**
   * Has the feed actually LOADED?
   *
   * Without this the baseline below is taken on the very first render, when
   * `rows` is still the empty array the query returns before it resolves. An
   * empty array is not a baseline — it is "we do not know yet" — so every
   * genuinely-old notification then counted as new the moment the query
   * landed, which is precisely the morning backlog the baseline exists to
   * prevent.
   */
  ready: boolean,
) {
  // Ids we have already considered. A ref, not state: changing it must never
  // re-render, or every poll would loop.
  const seen = useRef<Set<string> | null>(null);
  const openRef = useRef(onOpen);
  openRef.current = onOpen;

  useEffect(() => {
    // Nothing has loaded yet, so there is nothing to compare against and no
    // honest baseline to take. See `ready`.
    if (!ready) return;
    if (browserNotificationState() !== 'granted' || !desktopNotificationsEnabled()) return;

    // First loaded run: record what was already there and notify about none of
    // it.
    if (seen.current === null) {
      seen.current = new Set(rows.map((r) => r.id));
      return;
    }

    for (const row of rows) {
      if (seen.current.has(row.id)) continue;
      seen.current.add(row.id);
      if (row.readAt) continue; // already read elsewhere — another tab, or the API
      // No visibility check on purpose — see the header. They fire whether or
      // not the tab is in front (Kai).

      try {
        const n = new Notification(row.title, {
          body: row.body,
          // The id as the tag, so the same notification arriving from two open
          // tabs collapses into one rather than stacking.
          tag: row.id,
          // Never `requireInteraction`: a notification that will not go away
          // until clicked is the behaviour people cite when they turn these
          // off for good.
        });
        n.onclick = () => {
          window.focus();
          if (row.ticketId) openRef.current(row.ticketId);
          n.close();
        };
      } catch {
        // Some platforms throw on construction (Android Chrome requires a
        // service worker). Silence is right: the bell still has the entry,
        // and there is nothing an agent could do about it anyway.
      }
    }
  }, [rows, ready]);
}

/**
 * Ask for permission, from a click handler.
 *
 * Returns the resulting state so the caller can stop offering it. Wrapped
 * because Safari once took a callback rather than returning a promise, and the
 * throw that produces is otherwise an unhandled rejection in the console.
 */
export async function requestBrowserNotifications(): Promise<NotificationPermissionState> {
  if (browserNotificationState() === 'unsupported') return 'unsupported';
  try {
    const result = await Notification.requestPermission();
    if (result === 'granted') setDesktopNotificationsEnabled(true);
    return result as NotificationPermissionState;
  } catch {
    return browserNotificationState();
  }
}

/** Re-render when the permission changes, so the bell's offer disappears the
 *  moment it is accepted rather than on the next poll. */
export function useNotificationPermission(): [
  NotificationPermissionState,
  (s: NotificationPermissionState) => void,
] {
  const [state, setState] = useState<NotificationPermissionState>(() => browserNotificationState());
  return [state, setState];
}
